import './styles.scss';

import { Dayts, DaytsFormatEnum, getClasses, getProperty } from '@/utils';
import { ReactElement, memo, useMemo, useRef } from 'react';

import AddonTableHeader from './AddonTableHeader';
import AddonTableRow from './AddonTableRow';
import FooterDrawer from '@/components/FooterDrawer';
import { Import } from '@/models/gen/graphql';
import InlineImport from '@/pages/Manifests/InlineImport';
import Table from '@/common/Table';
import { useAddonsState } from '@/features/Addons';

const AddonTable = (): ReactElement => {
  const tableRef = useRef(null);
  const [{ imports, sortBy, sortDirection, expandedId, closeDrawer, drawerTitle, setFocus, scrollToFocus, focused }] = useAddonsState(
    ({ state, setState }) => {
      const currentImport = state.imports.find((i) => i.id === state.expandedId);
      return [
        {
          imports: state.imports,
          sortBy: state.table.sorting.column,
          sortDirection: state.table.sorting.direction,
          expandedId: state.expandedId,
          setFocus: state.setFocus,
          scrollToFocus: state.scrollToFocus,
          focused: state.focused,
          closeDrawer: () => setState((current) => ({ ...current, expandedId: undefined })),

          drawerTitle:
            [
              new Dayts(currentImport?.createdAt).toLocal().formatAs(DaytsFormatEnum.DATETIME_FE_SHORT),
              currentImport?.email?.from,
              currentImport?.email?.subject,
            ].join(' - ') || undefined,
        },
      ];
    }
  );

  const sortedRows = useMemo((): Import[] => {
    return [...imports].sort((a: Import, b: Import): number => {
      if (!sortBy || !sortDirection) return 0;
      const aValue = (getProperty(sortBy, a) as string) ?? '';
      const bValue = (getProperty(sortBy, b) as string) ?? '';
      const aLower = typeof aValue === 'string' ? aValue.toLowerCase() : aValue;
      const bLower = typeof bValue === 'string' ? bValue.toLowerCase() : bValue;
      if (aLower < bLower) return sortDirection === 'asc' ? -1 : 1;
      if (aLower > bLower) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    }) as unknown as Import[];
  }, [imports, sortBy, sortDirection]);

  return (
    <>
      <Table className={getClasses('AddonTable')} header={<AddonTableHeader />} onEnd={console.log} showControls ref={tableRef}>
        {!sortedRows.length && (
          <Table.Row style={{ gridTemplateColumns: '1fr' }}>
            <Table.Cell className="text-center">
              <i className="fa fa-inbox" /> <span>No Records</span>
            </Table.Cell>
          </Table.Row>
        )}
        {sortedRows.map(
          (importWithAirport: Import): ReactElement => (
            <AddonTableRow key={importWithAirport.id} {...importWithAirport} />
          )
        )}
      </Table>
      {expandedId && (
        <FooterDrawer
          className="InlineImportFooter"
          show={expandedId}
          onClose={closeDrawer}
          title={drawerTitle}
          initHeight={window.visualViewport.height / 2}
          hideMinimize
        >
          <div style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            <InlineImport
              importId={expandedId || ''}
              onSubmit={(importId: string): void => {
                setFocus(importId);
                scrollToFocus();
                closeDrawer();
              }}
              onCancel={closeDrawer}
            />
          </div>
        </FooterDrawer>
      )}
    </>
  );
};

export default memo(AddonTable);
